import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dimensions } from './hooks/useWindowDimensions';
import { animated, config, easings, to, useSpring, useSprings, useTransition } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import Spinner, { getSpinnerSize } from './Spinner';
import { replaceLinks } from './replaceLinks';
import { Theme } from './Themes';
import useSound from 'use-sound';
import flySfx from './sounds/fly.mp3';
import introSfx from './sounds/intro.mp3';
import selectSfx from './sounds/select.mp3';
import PieceSvg from './svg/PieceSvg';
import takeSfx from './sounds/take.mp3';
import dropSfx from './sounds/drop.mp3';

type MenuProps = {
	menuTexts: string[];
	cards: string[];
	selectedCard: string | undefined;
	selectedSizes: number[];
	onCardClick: (card: string) => void;
	onSizeClick: (size: number) => void;
	onCardsGone: () => void;
	theme: Theme;
	windowDim: Dimensions;
};

const cardImage = (title: string) => `/images/${title}/image_md.jpg`;
const cardTransform = (trz: number, r: number, rx: number) =>
	`perspective(1500px) translateZ(${trz}px) rotateX(${rx}deg) rotateY(${r / 10}deg) rotateZ(${r}deg)`;

const Menu = ({ menuTexts, cards, selectedCard, selectedSizes, onCardClick, onSizeClick, onCardsGone, theme, windowDim }: MenuProps) => {
	const [isLoading, setIsLoading] = useState(true);
	const isCardsDropped = useRef(false);
	const topCardIndex = useRef<number>(0);
	useEffect(() => {
		topCardIndex.current = cards.length - 1;
	}, [cards]);

	const prevSelectedCard = useRef<string | undefined>();

	const [shownSizes, setShownSizes] = useState<number[]>([]);
	useEffect(() => {
		setShownSizes(selectedSizes);
	}, [selectedSizes]);

	const sizeButtonsTransitions = useTransition(shownSizes, {
		from: { y: -50, opacity: 0 },
		enter: (size) => ({ y: 0, opacity: 1, delay: selectedSizes.indexOf(size) * 100 }),
		leave: { y: 50, opacity: 0 },
		config: config.stiff,
	});
	const [playFlySfx] = useSound(flySfx);
	const [playIntroSfx] = useSound(introSfx);
	const [playSelectSfx] = useSound(selectSfx);
	const [playTakeSfx] = useSound(takeSfx);
	const [playDropSfx] = useSound(dropSfx);

	const loadedImages = useRef(new Set());
	const onLoadImage = useCallback(
		(img: string) => {
			loadedImages.current.add(img);
			if (isLoading && cards.filter((card) => !loadedImages.current.has(card)).length === 0) {
				// setTimeout(() => setIsLoading(false), 2000);
				setIsLoading(false);
			}
		},
		[cards, isLoading, loadedImages]
	);

	useEffect(() => {
		if (cards.filter((card) => !loadedImages.current.has(card)).length === 0) {
			setIsLoading(false);
		}
	}, [cards, loadedImages]);

	const spinnerSize = useMemo(() => getSpinnerSize(windowDim, 300), [windowDim]);
	const cardWidth = useMemo(() => Math.min(600, 0.8 * Math.min(windowDim.width, windowDim.height)), [windowDim]);

	const [logoSpring, logoSpringApi] = useSpring(() => ({ opacity: 0 }));
	const [titleSpring, titleSpringApi] = useSpring(() => ({ opacity: 0 }));
	const [textSpring, textSpringApi] = useSpring(() => ({ opacity: 0 }));

	const cardFrom = useCallback(
		(i: number) => ({ x: 0, y: -windowDim.height, trz: ((i + 1) * cardWidth) / 100, rot: 0, rotX: 0, o: 1 }),
		[windowDim, cardWidth]
	);
	const cardTo = useCallback(
		(i: number) => ({
			x: 0,
			y: (-cardWidth / 100) * i,
			trz: (i * cardWidth) / 50,
			rot: -10 + 20 * Math.random(),
			rotX: 30,
			o: 1,
		}),
		[cardWidth]
	);

	// const cardsGone = useRef(new Set<number>());

	const [cardsSprings, cardsSpringsApi] = useSprings(cards.length, (i) => cardFrom(i));

	const onSizeTap = useCallback(
		(size: number) => {
			if (selectedCard) {
				titleSpringApi.start({ opacity: 0 });
				textSpringApi.start({ opacity: 0 });
				logoSpringApi.start({ opacity: 0 });
				cardsSpringsApi.start((i) =>
					i === topCardIndex.current
						? {
								to: [
									{ trz: 0.75 * cardWidth, config: { duration: 250, easing: easings.easeInCubic } },
									{ trz: 0, o: 0, config: { duration: 250, easing: easings.easeInCubic } },
								],
						  }
						: { o: 0, config: { duration: 150 } }
				);
				setShownSizes([]);
				playSelectSfx();
				setTimeout(() => {
					onSizeClick(size);
				}, 500);
			}
		},
		[selectedCard, onSizeClick, cardWidth, titleSpringApi, textSpringApi, logoSpringApi, cardsSpringsApi, playSelectSfx]
	);

	useEffect(() => {
		if (selectedCard) {
			prevSelectedCard.current = selectedCard;
			cardsSpringsApi.start((i) =>
				cards[i] === selectedCard
					? { to: { x: 0, y: 0, trz: 0.5 * cardWidth, rot: 0, rotX: 0, o: 1 }, config: { duration: 300, easing: easings.easeOutCubic } }
					: undefined
			);
			textSpringApi.start({ opacity: 0 });
			playTakeSfx();
		} else if (prevSelectedCard.current) {
			cardsSpringsApi.start((i) => (cards[i] === prevSelectedCard.current ? { to: cardTo(i) } : undefined));
			textSpringApi.start({ opacity: 1 });
			playDropSfx();
		}
	}, [selectedCard, cards, cardsSpringsApi, textSpringApi, cardTo, cardWidth, onSizeTap, playTakeSfx, playDropSfx]);

	useEffect(() => {
		if (!isLoading && !isCardsDropped.current) {
			isCardsDropped.current = true;
			if (cards.length === 1) {
				logoSpringApi.start({ opacity: 1 });
				titleSpringApi.start({ opacity: 1 });
				textSpringApi.start({ opacity: 1 });
				cardsSpringsApi.start((i) => ({ to: cardTo(i), immediate: true }));
				onCardClick(cards[0]);
			} else {
				logoSpringApi.start({ opacity: 1 });
				titleSpringApi.start({ opacity: 1, delay: 1000 });
				textSpringApi.start({ opacity: 1, delay: 1500 });
				cardsSpringsApi.start((i) => ({
					from: cardFrom(i),
					to: cardTo(i),
					delay: 500 + i * 50,
					config: { ...config.default, tension: 210 },
				}));
			}
			playIntroSfx();
		}
	}, [isLoading, cards, logoSpringApi, titleSpringApi, textSpringApi, cardsSpringsApi, cardTo, cardFrom, onCardClick, playIntroSfx]);

	const drag = useDrag(
		({ args: [index], active, movement: [mx, my], velocity: [vx, vy], direction: [dx, dy], tap }) => {
			if (index !== topCardIndex.current) {
			} else if (tap) {
				onCardClick(cards[index]);
			} else if (selectedSizes.length > 0) {
			} else if (active) {
				cardsSpringsApi.start((i) =>
					i === index
						? {
								x: mx,
								y: my,
								trz: (cards.length * cardWidth) / 50 + cardWidth / 2,
								rot: 0,
								rotX: 10,
								config: { ...config.default, friction: 50, tension: 500 },
						  }
						: undefined
				);
			} else if (cards.length === 1) {
				onCardClick(cards[index]);
			} else {
				let toX = 0;
				let toY = 0;
				if (vx * vx + vy * vy > 0.1) {
					if (Math.abs(vx) > Math.abs(vy)) {
						toX = dx * windowDim.width;
						toY = ((dy * vy) / vx) * windowDim.width;
					} else {
						toY = dy * windowDim.height;
						toX = ((dx * vx) / vy) * windowDim.height;
					}
					topCardIndex.current = index - 1;
					// cardsGone.current.add(index);
					playFlySfx();
				} else if (Math.abs(mx) > cardWidth / 2 || Math.abs(my) > cardWidth / 2) {
					if (Math.abs(mx) > Math.abs(my)) {
						toX = Math.sign(mx) * windowDim.width;
						toY = Math.sign(my) * Math.abs(my / mx) * windowDim.width;
					} else {
						toY = Math.sign(my) * windowDim.height;
						toX = Math.sign(mx) * Math.abs(mx / my) * windowDim.height;
					}
					topCardIndex.current = index - 1;
					// cardsGone.current.add(index);
					playFlySfx();
				}
				// const gone = cardsGone.current.has(index);
				cardsSpringsApi.start((i) =>
					i === index
						? topCardIndex.current === index - 1
							? { x: toX, y: toY, trz: (i * cardWidth) / 50, config: config.slow }
							: { to: cardTo(i), config: config.default }
						: undefined
				);
				if (topCardIndex.current < 0) {
					setIsLoading(true);
					isCardsDropped.current = false;
					logoSpringApi.start({ opacity: 0 });
					setTimeout(onCardsGone, 500);
				}
				// if (cardsGone.current.size === cards.length) {
				// 	cardsGone.current.clear();
				// 	setIsLoading(true);
				// 	setTimeout(onCardsGone, 500);
				// }
			}
		},
		{ filterTaps: true }
	);

	return (
		<>
			<div
				className="absolute flex select-none items-center justify-center"
				style={{
					width: 1.25 * cardWidth,
					height: 1.25 * cardWidth,
					left: (windowDim.width - 1.25 * cardWidth) / 2,
					top: (windowDim.height - 1.25 * cardWidth) / 2,
				}}>
				<animated.div className="absolute top-0 flex h-1/6 w-full items-center justify-center" style={titleSpring}>
					<h1 className="font-karuselbold drop-shadow-sm lg:drop-shadow" style={{ fontSize: cardWidth / 10, color: theme.background.text }}>
						{menuTexts[0]}
					</h1>
				</animated.div>
				<animated.div className="absolute bottom-0 flex h-1/5 w-full flex-col items-center justify-center" style={textSpring}>
					{menuTexts.map(
						(text, i) =>
							i > 0 && (
								<p
									key={'text' + i}
									className="text-center font-karusel drop-shadow-sm lg:drop-shadow-md"
									style={{ fontSize: cardWidth / 20, color: theme.background.text }}>
									{replaceLinks(text, theme.background.link)}
								</p>
							)
					)}
				</animated.div>
				{theme.logo && <animated.img src={theme.logo} alt="" style={{ ...logoSpring, width: cardWidth / 2, height: cardWidth / 2 }} />}
				{cardsSprings.map(({ x, y, trz, rot, rotX, o }, i) => (
					<animated.div
						key={'card-' + cards[i]}
						{...drag(i)}
						className="absolute touch-none bg-white shadow-xl"
						style={{
							x,
							y,
							width: cardWidth,
							height: 0.75 * cardWidth,
							transform: to([trz, rot, rotX], cardTransform),
							borderRadius: 0.04 * cardWidth,
							padding: 0.01 * cardWidth,
							opacity: o,
						}}>
						<img
							src={cardImage(cards[i])}
							alt={cards[i]}
							className="pointer-events-none h-full w-full select-none object-cover"
							style={{
								borderRadius: 0.03 * cardWidth,
							}}
							onLoad={() => onLoadImage(cards[i])}
							onError={() => onLoadImage(cards[i])}
						/>
						{/*<p className="absolute top-4 left-4 text-3xl font-bold text-white">{i}</p>*/}
					</animated.div>
				))}

				<div
					className="absolute flex justify-evenly"
					style={{ transform: `translateZ(${cardWidth}px) translateY(${cardWidth / 5}px)`, width: 1.25 * cardWidth }}>
					{sizeButtonsTransitions((animatedStyle, size, _, i) => (
						<animated.div
							className="relative flex cursor-pointer items-center justify-center shadow-xl"
							style={{
								backgroundColor: theme.primary.color,
								width: 0.28 * cardWidth,
								height: 0.66 * 0.28 * cardWidth,
								borderRadius: cardWidth / 20,
								...animatedStyle,
							}}
							onClick={() => onSizeTap(i)}>
							<p className="align-middle font-karuselbold" style={{ fontSize: cardWidth / 9, color: theme.primary.text }}>
								{size}
							</p>
							<PieceSvg width={cardWidth / 10} height={cardWidth / 10} fill={theme.primary.text} />
						</animated.div>
					))}
				</div>
			</div>
			<Spinner size={spinnerSize} color={theme.primary.color} enabled={isLoading} />
		</>
	);
};

export default Menu;
